import React, { useEffect, useState } from "react";
import { Typography, Button, Space, Row, Col, Card, Divider } from "antd";
import { ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import notFoundImage from "../../assets/images/not_found.png";
import { mergeDecrypted } from "../../utils/decrypt";

const { Title, Text } = Typography;

const ShipmentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { request } = useRequest();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get token from localStorage
  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("token")
    );
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `$${numeric.toFixed(2)}` : `$${value}`;
  };

  const normalizeImageUrl = (url) => {
    if (!url) return "";
    if (Array.isArray(url)) return normalizeImageUrl(url.find(Boolean));
    if (typeof url === "object") {
      return normalizeImageUrl(
        url.url ||
          url.image ||
          url.image_url ||
          url.imageUrl ||
          url.src ||
          url.path,
      );
    }

    const trimmed = String(url).trim();
    if (!trimmed) return "";
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:") ||
      trimmed.startsWith("blob:")
    )
      return trimmed;
    if (trimmed.startsWith("//")) return window.location.protocol + trimmed;
    return `${apiPath.baseURL.replace(/\/$/, "")}/${trimmed.replace(/^\//, "")}`;
  };

  const normalizeOrderPayload = (data) => {
    // Handle different response structures
    if (data?.data?.order) return data.data.order;
    if (data?.order) return data.order;
    if (data?.data) return data.data;
    return data;
  };

  const getItems = (orderData) => {
    return (
      orderData?.items || orderData?.order_items || orderData?.products || []
    );
  };

  const getItemName = (item) => {
    return (
      item?.name ||
      item?.product_name ||
      item?.title ||
      item?.product?.name ||
      item?.product?.title ||
      "Product"
    );
  };

  const getItemSku = (item) => {
    return (
      item?.sku ||
      item?.product_sku ||
      item?.product_source_id ||
      item?.product?.sku ||
      item?.product?.product_sku ||
      "N/A"
    );
  };

  const getItemQty = (item) => {
    return item?.quantity || item?.qty || item?.count || 1;
  };

  const getItemPrice = (item) => {
    const value =
      item?.unit_price ??
      item?.price ??
      item?.sale_price ??
      item?.subtotal ??
      item?.total_amount ??
      item?.line_total ??
      item?.product?.price;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : (value ?? "-");
  };

  const getItemImage = (item) => {
    const candidates = [
      item?.image,
      item?.image_url,
      item?.imageUrl,
      item?.product_image,
      item?.thumbnail,
      item?.thumbUrl,
      item?.url,
      item?.product?.image,
      item?.product?.image_url,
      item?.product?.imageUrl,
      item?.product?.thumbnail,
      item?.product?.images,
    ];
    const found = candidates.find((src) =>
      Array.isArray(src)
        ? src.length > 0
        : typeof src === "string"
          ? src.trim() !== ""
          : Boolean(src),
    );
    return normalizeImageUrl(found) || notFoundImage;
  };

  useEffect(() => {
    const token = getToken();

    if (!token) {
      console.warn("No token found in localStorage");
      setLoading(false);
      navigate("/login");
      return;
    }

    // Using the specific API path for order details
    const orderDetailsUrl = apiPath.orderDetails(id);

    request({
      url: orderDetailsUrl,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      onSuccess: async (data) => {
        const normalizedOrder = normalizeOrderPayload(data);
        const decryptedOrder = await mergeDecrypted(normalizedOrder);
        setOrder(decryptedOrder);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching shipment details:", error);
        if (error?.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem("token");
          localStorage.removeItem("access_token");
          sessionStorage.removeItem("token");
          navigate("/login");
        }
        setLoading(false);
      },
    });
  }, [id, request, navigate]);

  if (loading) {
    return (
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          minHeight: "100vh",
          background: "#fff",
        }}
      >
        <Text>Loading shipment details...</Text>
      </div>
    );
  }

  if (!order) {
    return (
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          minHeight: "100vh",
          background: "#fff",
        }}
      >
        <Text>Shipment not found</Text>
        <div style={{ marginTop: "16px" }}>
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const items = getItems(order);
  const total =
    order.grand_total ?? order.total ?? order.amount ?? order.total_amount ?? 0;

  return (
    <div
      className="shipment-details-container"
      style={{ padding: "24px", background: "#fff", minHeight: "100vh" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <Title level={4} style={{ margin: 0, fontWeight: 700 }}>
          Shipment #{order.order_number || order.id || id}
        </Title>
        <Button
          type="text"
          onClick={() => navigate(-1)}
          style={{ color: "#64748b", fontWeight: 500 }}
        >
          Back
        </Button>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card
            bordered
            style={{ marginBottom: "24px", borderRadius: "8px" }}
            bodyStyle={{ padding: 0 }}
          >
            <div
              style={{
                padding: "16px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #f0f0f0",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <Text strong style={{ color: "#334155" }}>
                Ordered Items ({items.length || 0})
              </Text>
              <Text strong style={{ color: "#334155" }}>
                Grand Total - {formatMoney(total)}
              </Text>
            </div>

            {items.length > 0 ? (
              items.map((item, index) => (
                <div
                  key={`${getItemSku(item)}-${index}`}
                  style={{
                    padding: "24px",
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom:
                      index !== items.length - 1 ? "1px solid #f0f0f0" : "none",
                    flexWrap: "wrap",
                  }}
                >
                  <Space align="start" size="large" wrap>
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        background: "#f1f5f9",
                        borderRadius: 6,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={getItemImage(item)}
                        alt={getItemName(item)}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = notFoundImage;
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <Text strong style={{ color: "#0f172a" }}>
                        {getItemName(item)}
                      </Text>
                      <Text style={{ color: "#64748b", fontSize: "13px" }}>
                        {formatMoney(getItemPrice(item))} Per Unit x{" "}
                        {getItemQty(item)} Quantity
                      </Text>
                      <Text style={{ color: "#64748b", fontSize: "13px" }}>
                        SKU - {getItemSku(item)}
                      </Text>
                      <Text style={{ color: "#64748b", fontSize: "13px" }}>
                        Ordered ({getItemQty(item)})
                      </Text>
                    </div>
                  </Space>
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "24px",
                  color: "#64748b",
                  textAlign: "center",
                }}
              >
                No items found for this shipment.
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card bordered style={{ borderRadius: "8px", height: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <Text strong style={{ color: "#64748b" }}>
                Customer
              </Text>
              <ChevronRight
                size={16}
                color="#94a3b8"
                style={{ transform: "rotate(-90deg)" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                marginBottom: "24px",
              }}
            >
              <Text style={{ color: "#0f172a" }}>
                {order.customer_name ||
                  order.user?.name ||
                  order.customer?.name ||
                  "John Doe"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.customer_email ||
                  order.user?.email ||
                  order.customer?.email ||
                  "john@example.com"}
              </Text>
            </div>
            <Divider style={{ margin: "16px 0" }} />

            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ color: "#64748b" }}>
                Billing Address
              </Text>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                marginBottom: "24px",
              }}
            >
              <Text strong style={{ color: "#0f172a" }}>
                {order.billing_company || order.billing_name || "N/A"}
              </Text>
              <Text strong style={{ color: "#0f172a" }}>
                {order.billing_contact || order.billing_name || "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.billing_address_line1 ||
                  order.shipping_address_line1 ||
                  "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.billing_city || order.shipping_city || "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.billing_country || order.shipping_country || "N/A"}
              </Text>
            </div>
            <Divider style={{ margin: "16px 0" }} />

            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ color: "#64748b" }}>
                Shipping Address
              </Text>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <Text strong style={{ color: "#0f172a" }}>
                {order.shipping_company || "N/A"}
              </Text>
              <Text strong style={{ color: "#0f172a" }}>
                {order.shipping_name || "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.shipping_address_line1 || "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.shipping_city || "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.shipping_country || "N/A"}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ShipmentDetails;
